import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Input,
  notification,
  Popconfirm,
  Spin,
  Table,
  Tag,
  Tooltip,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadFile } from 'antd/es/upload/interface';
import { InboxOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../layouts/default/Default.layout';
import usePageTitle from '../../core/hooks/UsePageTitle';
import { PdfDocument } from '../../sdk/@types/Acertei';
import FileService from '../../sdk/services/FileService';
import PdfDocumentService from '../../sdk/services/PdfDocumentService';
import PdfHighlightService from '../../sdk/services/PdfHighlightService';
import AccessDenied from '../components/AccessDenied/AccessDenied';
import { readApiError, type ApiError } from '../../sdk/errors/apiError';

const { Dragger } = Upload;

interface ReviewStatus {
  total: number;
  pending: number;
}

interface DocRow extends PdfDocument {
  reviewStatus?: ReviewStatus;
  loadingStatus?: boolean;
}

export default function FileManager() {
  usePageTitle('Gerenciador de PDFs');
  const navigate = useNavigate();

  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(false);
  // Um 403 aqui bloqueia a tela inteira, não só a tabela: quem não pode listar
  // também não pode importar nem substituir.
  const [denial, setDenial] = useState<ApiError | null>(null);
  const [uploading, setUploading] = useState(false);

  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');

  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const [replacingDocId, setReplacingDocId] = useState<string | null>(null);

  const loadDocs = async () => {
    setLoading(true);
    try {
      // Antes isto era `.catch(() => null)`, e um 403 virava uma tabela vazia —
      // o aluno concluía que não havia PDF nenhum cadastrado.
      const list = await PdfDocumentService.list();
      setDocs(list.map(d => ({ ...d })));
      setDenial(null);
    } catch (error) {
      const apiError = readApiError(error);
      if (apiError.kind) setDenial(apiError);
      else notification.error({ message: 'Não foi possível carregar os PDFs.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
  }, []);

  const loadReviewStatus = async (doc: DocRow) => {
    setDocs(prev =>
      prev.map(d => d.ID === doc.ID ? { ...d, loadingStatus: true } : d)
    );
    try {
      const status = await PdfDocumentService.reviewStatus(doc.ID);
      setDocs(prev =>
        prev.map(d =>
          d.ID === doc.ID
            ? { ...d, reviewStatus: status, loadingStatus: false }
            : d
        )
      );
    } catch {
      setDocs(prev =>
        prev.map(d => d.ID === doc.ID ? { ...d, loadingStatus: false } : d)
      );
    }
  };

  const handleUpload = async () => {
    if (!pendingFile || !title.trim()) {
      notification.warning({ message: 'Informe o título e selecione um arquivo PDF.' });
      return;
    }
    setUploading(true);
    try {
      const { file_key } = await FileService.upload(pendingFile, 'pdf');
      await PdfDocumentService.create(title.trim(), file_key);
      notification.success({ message: 'PDF importado com sucesso.' });
      setPendingFile(null);
      setTitle('');
      await loadDocs();
    } catch {
      notification.error({ message: 'Erro ao importar PDF.' });
    } finally {
      setUploading(false);
    }
  };

  const handleReplace = async (file: File, docId: string) => {
    setUploading(true);
    try {
      const { file_key } = await FileService.upload(file, 'pdf');
      await PdfDocumentService.replace(docId, file_key);
      notification.success({ message: 'PDF substituído. Highlights pendentes de revisão.' });
      await loadDocs();
    } catch {
      notification.error({ message: 'Erro ao substituir PDF.' });
    } finally {
      setUploading(false);
      setReplacingDocId(null);
    }
  };

  const handleDelete = async (doc: DocRow) => {
    try {
      const highlights = await PdfHighlightService.getByPdf(doc.ID).catch(() => []);
      await Promise.all(highlights.map(h => PdfHighlightService.delete(h.highlight_id).catch(() => {})));
      await FileService.delete(doc.FileKey);
      await PdfDocumentService.delete(doc.ID);
      setDocs(prev => prev.filter(d => d.ID !== doc.ID));
      notification.success({
        message: 'PDF excluído.',
        description: highlights.length > 0 ? `${highlights.length} marca(s) removida(s).` : undefined,
      });
    } catch {
      notification.error({ message: 'Erro ao excluir PDF.' });
    }
  };

  const columns: ColumnsType<DocRow> = [
    {
      title: 'Título',
      dataIndex: 'Title',
      key: 'Title',
      render: (text: string) => (
        <span className="font-medium text-gray-900 dark:text-white">{text}</span>
      ),
    },
    {
      title: 'Criado em',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 160,
      render: (val: string) =>
        val ? new Date(val).toLocaleDateString('pt-BR') : '—',
    },
    {
      title: 'Highlights',
      key: 'status',
      width: 160,
      render: (_: unknown, doc: DocRow) => {
        if (doc.loadingStatus) return <Spin size="small" />;
        if (!doc.reviewStatus) {
          return (
            <Button
              size="small"
              type="link"
              style={{ padding: 0, color: '#6b8f6e' }}
              onClick={() => loadReviewStatus(doc)}
            >
              Ver status
            </Button>
          );
        }
        const { total, pending } = doc.reviewStatus;
        return (
          <span className="text-sm">
            {pending > 0 ? (
              <Tag color="orange">{pending} pendentes / {total}</Tag>
            ) : (
              <Tag color="green">{total} revisados</Tag>
            )}
          </span>
        );
      },
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 280,
      render: (_: unknown, doc: DocRow) => (
        <div className="flex flex-wrap gap-2">
          <Tooltip title="Abrir no anotador">
            <Button
              size="small"
              onClick={() => navigate(`/admin/pdf-annotator?docId=${doc.ID}`)}
            >
              Anotar
            </Button>
          </Tooltip>

          <Tooltip title="Revisar posições de highlights">
            <Button
              size="small"
              onClick={() =>
                navigate(`/admin/pdf-annotator?docId=${doc.ID}&reviewMode=true`)
              }
            >
              Revisar
            </Button>
          </Tooltip>

          <Tooltip title="Substituir arquivo PDF (mantém highlights, marca como pendente)">
            <Button
              size="small"
              loading={uploading && replacingDocId === doc.ID}
              onClick={() => {
                setReplacingDocId(doc.ID);
                replaceInputRef.current?.click();
              }}
            >
              Substituir
            </Button>
          </Tooltip>

          <Popconfirm
            title="Excluir este PDF, todas as marcações vinculadas e o arquivo do disco?"
            onConfirm={() => handleDelete(doc)}
            okText="Excluir"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger>
              Excluir
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  if (denial) {
    return (
      <DefaultLayout>
        <div className="p-6">
          <AccessDenied
            kind={denial.kind!}
            detail={denial.raw}
            variant="panel"
            onNavigate={navigate}
          />
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="p-6 flex flex-col gap-8">
        {/* Upload panel */}
        <div className="bg-white dark:bg-[#0f1a10] border border-gray-200 dark:border-[#1a2e1c] rounded-lg p-6">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Importar novo PDF</h2>
          <div className="flex flex-col gap-4 max-w-xl">
            <Input
              placeholder="Título do documento"
              value={title}
              onChange={e => setTitle(e.target.value)}
              size="middle"
            />
            <Dragger
              accept=".pdf"
              multiple={false}
              beforeUpload={file => {
                setPendingFile(file);
                return false;
              }}
              onRemove={() => setPendingFile(null)}
              fileList={
                pendingFile
                  ? [{ uid: '1', name: pendingFile.name, status: 'done' } as UploadFile]
                  : []
              }
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: '#a3e635' }} />
              </p>
              <p className="ant-upload-text text-gray-900 dark:text-white">
                Arraste o PDF aqui ou clique para selecionar
              </p>
            </Dragger>
            <Button
              type="primary"
              loading={uploading}
              disabled={!pendingFile || !title.trim()}
              onClick={handleUpload}
              style={{ background: '#a3e635', borderColor: '#a3e635', color: '#000', fontWeight: 600 }}
            >
              Importar PDF
            </Button>
          </div>
        </div>

        {/* Documents table */}
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            Documentos cadastrados
          </h2>
          <Table
            columns={columns}
            dataSource={docs}
            rowKey="ID"
            loading={loading}
            pagination={{ pageSize: 20 }}
            locale={{ emptyText: 'Nenhum PDF cadastrado ainda.' }}
          />
        </div>
      </div>

      {/* Hidden input for PDF replacement */}
      <input
        ref={replaceInputRef}
        type="file"
        accept=".pdf"
        style={{ display: 'none' }}
        onChange={e => {
          const file = e.target.files?.[0];
          if (file && replacingDocId) handleReplace(file, replacingDocId);
          e.target.value = '';
        }}
      />
    </DefaultLayout>
  );
}
